import React, { lazy } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';

import { setRecoilExternalState } from '@bo-recoil/RecoilExternalStatePortal';
import { siteState, subsidiaryState } from '@bo-recoil/common/atom';
import siteApi from '@bo-apis/system/siteApi';
import MainPage from '@bo-pages/MainPage';
import Home from '@bo-pages/Home';
import LogoutPage from '@bo-pages/LogoutPage';
import SelectJobGroupPage from '@bo-pages/common/SelectJobGroupPage';
import UnauthorizedErrorPage from '@bo-pages/error/UnauthorizedErrorPage';
import NotFoundErrorPage from '@bo-pages/error/NotFoundErrorPage';
import ClientServerErrorPage from '@bo-pages/error/ClientServerErrorPage';
// system
import SiteManagementDetailMinor from '@bo-pages/site/SiteManagementDetailMinor';
import GridTestPage from './GridTestPage';
import RootRoute from './RootRoute';
const CommonCodePage = lazy(() => import('@bo-pages/system/CommonCodePage'));
const BatchManagementPage = lazy(() => import('@bo-pages/system/batch/BatchManagementPage'));
const BatchExecutePage = lazy(() => import('@bo-pages/system/batch/BatchExecutePage'));
const HolidayPage = lazy(() => import('@bo-pages/system/HolidayPage'));
const JobGroupPage = lazy(() => import('@bo-pages/user/JobGroupPage'));
const TermsManagementPage = lazy(() => import('@bo-pages/system/TermsManagementPage'));
const TermsManagementDetail = lazy(() => import('@bo-pages/system/TermsManagementDetail'));
const MenuManagementPage = lazy(() => import('@bo-pages/system/menu/MenuManagementPage'));
const PageManagementPage = lazy(() => import('@bo-pages/system/menu/PageManagementPage'));
const ProhibitedWordPage = lazy(() => import('@bo-pages/system/ProhibitedWordPage'));
const SiteManagementPage = lazy(() => import('@bo-pages/site/SiteManagementPage'));
const SiteManagementEdit = lazy(() => import('@bo-pages/site/SiteManagementEdit'));
const SiteManagementDetail = lazy(() => import('@bo-pages/site/SiteManagementDetail'));
const GiftishowManagementPage = lazy(() => import('@bo-pages/system/giftishow/GiftishowManagementPage'));
const AlimTalkManagementPage = lazy(() => import('@bo-pages/system/alimTalk/AlimTalkManagementPage'));
const AlimTalkManagementDetail = lazy(() => import('@bo-pages/system/alimTalk/AlimTalkManagementDetail'));
// member
const MemberPage = lazy(() => import('@bo-pages/member/MemberPage'));
const MemberDetailPage = lazy(() => import('@bo-pages/member/MemberDetailPage'));
const MemberGradePage = lazy(() => import('@bo-pages/member/MemberGradePage'));
// promo
const ExhibitionListPage = lazy(() => import('@bo-pages/exhibition/ExhibitionListPage'));
const ExhibitionDetailPage = lazy(() => import('@bo-pages/exhibition/ExhibitionDetailPage'));
const IntegrationExhibitionRegistrationPage = lazy(() =>
  import('@bo-pages/exhibition/IntegrationExhibitionRegistrationPage')
);
const AllianceExhibitionDetailPage = lazy(() => import('@bo-pages/exhibition/AllianceExhibitionDetailPage'));
const TimeSaleManagementPage = lazy(() => import('@bo-pages/timeSale/TimeSaleManagementPage'));
const EventListPage = lazy(() => import('@bo-pages/event/EventListPage'));
const EventDetailPage = lazy(() => import('@bo-pages/event/EventDetailPage'));
const PromotionListPage = lazy(() => import('@bo-pages/promotion/PromotionListPage'));
const PromotionDetailPage = lazy(() => import('@bo-pages/promotion/PromotionDetailPage'));
const PromotionApprovalManagementPage = lazy(() => import('@bo-pages/promotion/PromotionApprovalManagementPage'));
const PromotionApprovalDetailPage = lazy(() => import('@bo-pages/promotion/PromotionApprovalDetailPage'));
const PromotionBaseLimitManagementPage = lazy(() => import('@bo-pages/promotion/PromotionBaseLimitManagementPage'));
const PromotionBaseLimitDetailPage = lazy(() => import('@bo-pages/promotion/PromotionBaseLimitDetailPage'));
const DMTemplateManagementPage = lazy(() => import('@bo-pages/promotion/DMTemplateManagementPage'));
const DMTemplateDetailPage = lazy(() => import('@bo-pages/promotion/DMTemplateDetailPage'));
const PromotionApprovalHistoryDetailPage = lazy(() => import('@bo-pages/promotion/PromotionApprovalHistoryDetailPage'));
const MarketingChannelPage = lazy(() => import('@bo-pages/promotion/MarketingChannelPage'));
const MarketingChannelDetailPage = lazy(() => import('@bo-pages/promotion/MarketingChannelDetailPage'));
// good
const BrandPage = lazy(() => import('@bo-pages/good/base/brand/BrandPage'));
const StandardCategoryPage = lazy(() => import('@bo-pages/good/base/standardCategory/StandardCategoryPage'));
const DeliveryAreaPage = lazy(() => import('@bo-pages/good/base/deliveryArea/DeliveryAreaPage'));
const GoodManagementPage = lazy(() => import('@bo-pages/good/info/management/GoodManagementPage'));
const GoodRegistrationPage = lazy(() => import('@bo-pages/good/info/registration/GoodRegistrationPage'));
const GoodDetailPage = lazy(() => import('@bo-pages/good/info/modification/GoodDetailPage'));
const GroupGoodDetailPage = lazy(() => import('@bo-pages/good/info/modification/GroupGoodDetailPage'));
const GroupGoodRegistrationPage = lazy(() => import('@bo-pages/good/info/registration/GroupGoodRegistrationPage'));
const TempGoodModificationPage = lazy(() => import('@bo-pages/good/info/modification/TempGoodModificationPage'));
const TempGroupGoodModificationPage = lazy(() =>
  import('@bo-pages/good/info/modification/TempGroupGoodModificationPage')
);
const TempGoodDetailPage = lazy(() => import('@bo-pages/good/info/modification/TempGoodDetailPage'));
const TempGoodMdRmPage = lazy(() => import('@bo-pages/good/info/modification/TempGoodMdRmPage'));
const TempGroupGoodDetailPage = lazy(() => import('@bo-pages/good/info/modification/TempGroupGoodDetailPage'));
const TempGroupGoodMdRmPage = lazy(() => import('@bo-pages/good/info/modification/TempGroupGoodMdRmPage'));
const GoodGroupModificationPage = lazy(() => import('@bo-pages/good/info/modification/GroupGoodModificationPage'));
const GoodModificationPage = lazy(() => import('@bo-pages/good/info/modification/GoodModificationPage'));
const ItemManagementPage = lazy(() => import('@bo-pages/good/info/item/ItemManagementPage'));
const ApprovalHistoryManagementPage = lazy(() =>
  import('@bo-pages/good/approval/management/ApprovalHistoryManagementPage')
);
const ApprovalManagementMdPage = lazy(() => import('@bo-pages/good/approval/management/ApprovalManagementMdPage'));
const ApprovalManagementRmPage = lazy(() => import('@bo-pages/good/approval/management/ApprovalManagementRmPage'));
const OfficialAnnouncementManagementPage = lazy(() =>
  import('@bo-pages/good/base/officialAnnouncement/OfficialAnnouncementManagementPage')
);
const OfficialAnnouncementInquiryPage = lazy(() =>
  import('@bo-pages/good/base/officialAnnouncementInquiry/OfficialAnnouncementInquiryPage')
);
const GoodReviewManagementPage = lazy(() => import('@bo-pages/goodReview/GoodReviewManagementPage'));
const GoodReviewDetailPage = lazy(() => import('@bo-pages/goodReview/GoodReviewDetailPage'));
const BatchGoodRegistrationPage = lazy(() => import('@bo-pages/good/info/batch/BatchGoodRegistrationPage'));
const TicketGoodManagementPage = lazy(() => import('@bo-pages/good/info/ticket/TicketGoodManagementPage'));
const TicketModificationPage = lazy(() => import('@bo-pages/good/info/ticket/TicketModificationPage'));
const SoldOutRequestManagePage = lazy(() => import('@bo-pages/good/info/soldout/SoldOutRequestManagePage'));
// display
const BannerManagementPage = lazy(() => import('@bo-pages/display/banner/BannerManagementPage'));
const BannerRegistrationPage = lazy(() => import('@bo-pages/display/banner/BannerRegistrationPage'));
const DisplayCategoryPage = lazy(() => import('@bo-pages/display/displayCategoryTemplate/DisplayCategoryTemplatePage'));
const DisplayCategoryTemplateRegistration = lazy(() =>
  import('@bo-pages/display/displayCategoryTemplate/components/template/DisplayCategoryTemplateRegistration')
);
const MainTemplatePage = lazy(() => import('@bo-pages/display/mainTemplate/MainTemplatePage'));
const MainTemplateRegistration = lazy(() =>
  import('@bo-pages/display/mainTemplate/components/template/MainTemplateRegistration')
);
const MainTemplateContent = lazy(() => import('@bo-pages/display/mainTemplate/components/content/MainTemplateContent'));

const DisplayCategoryModification = lazy(() =>
  import('@bo-pages/display/displayCategoryTemplate/components/category/DisplayCategoryModification')
);
// customer-service
const NotificationPage = lazy(() => import('@bo-pages/customerService/notification/NotificationPage'));
const NotificationRegistrationPage = lazy(() =>
  import('@bo-pages/customerService/notification/NotificationRegistrationPage')
);
const FaqManagementPage = lazy(() => import('@bo-pages/customerService/faq/FaqManagementPage'));
const FaqRegistrationPage = lazy(() => import('@bo-pages/customerService/faq/FaqRegistrationPage'));
const CounselingManagementPage = lazy(() => import('@bo-pages/customerService/counseling/CounselingManagementPage'));
const OutboundManagementPage = lazy(() => import('@bo-pages/customerService/outbound/OutboundManagementPage'));
const OutboundDetailPage = lazy(() => import('@bo-pages/customerService/outbound/OutboundDetailPage'));
const CounselingApprovalManagementPage = lazy(() =>
  import('@bo-pages/customerService/counseling/CounselingApprovalManagementPage')
);
const CounselingApprovalRegistrationPage = lazy(() =>
  import('@bo-pages/customerService/counseling/CounselingApprovalRegistrationPage')
);
const CounselingRegistrationPage = lazy(() =>
  import('@bo-pages/customerService/counseling/CounselingRegistrationPage')
);
const CounselingPhraseManagementPage = lazy(() =>
  import('@bo-pages/customerService/counselingPhrase/CounselingPhraseManagementPage')
);
const CounselingPhraseRegistrationPage = lazy(() =>
  import('@bo-pages/customerService/counselingPhrase/CounselingPhraseRegistrationPage')
);
// mall question
const EntryQuestionPage = lazy(() => import('@bo-pages/customerService/mallQuestion/EntryQuestionPage'));
const EntryQuestionDetailPage = lazy(() => import('@bo-pages/customerService/mallQuestion/EntryQuestionDetailPage'));
const BulkPurchaseQuestionPage = lazy(() => import('@bo-pages/customerService/mallQuestion/BulkPurchaseQuestionPage'));
const BulkPurchaseQuestionDetailPage = lazy(() =>
  import('@bo-pages/customerService/mallQuestion/BulkPurchaseQuestionDetailPage')
);
const AllianceQuestionPage = lazy(() => import('@bo-pages/customerService/mallQuestion/AllianceQuestionPage'));
const AllianceQuestionDetailPage = lazy(() =>
  import('@bo-pages/customerService/mallQuestion/AllianceQuestionDetailPage')
);
const ChargerManagementPage = lazy(() => import('@bo-pages/customerService/mallQuestion/ChargerManagementPage'));
// good settlement
const GoodSettlementPage = lazy(() => import('@bo-pages/settlement/GoodSettlementPage'));
const SettlementCarryOverPage = lazy(() => import('@bo-pages/settlement/SettlementCarryOver'));
const ManualSettlementPage = lazy(() => import('@bo-pages/settlement/ManualSettlementPage'));
const MonthlySettlementPage = lazy(() => import('@bo-pages/settlement/MonthlySettlementPage'));
const VendorSettlementClosePage = lazy(() => import('@bo-pages/settlement/VendorSettlementClosePage'));
const BlueverySettlementPage = lazy(() => import('@bo-pages/settlement/BlueverySettlementPage'));
// point
const PointGroupManagementPage = lazy(() => import('@bo-pages/point/PointGroupManagementPage'));
const PointGroupManagementDetail = lazy(() => import('@bo-pages/point/PointGroupManagementDetail'));
const PointApprovalManagementPage = lazy(() => import('@bo-pages/point/PointApprovalManagementPage'));
const PointApprovalManagementDetail = lazy(() => import('@bo-pages/point/PointApprovalManagementDetail'));
const PointRegistrationPage = lazy(() => import('@bo-pages/point/PointRegistrationPage'));
const PointSubtractRegistrationPage = lazy(() => import('@bo-pages/point/PointSubtractRegistrationPage'));
const PointRenewalRegistrationPage = lazy(() => import('@bo-pages/point/PointRenewalRegistrationPage'));
const PointSaveInfoPage = lazy(() => import('@bo-pages/point/PointSaveInfoPage'));
const PointMemberSaveInfoPage = lazy(() => import('@bo-pages/point/PointMemberSaveInfoPage'));
const ApprovalTemplatePage = lazy(() => import('@bo-pages/point/ApprovalTemplatePage'));
const ApprovalTemplateDetail = lazy(() => import('@bo-pages/point/ApprovalTemplateDetail'));
const PointAccumulatePage = lazy(() => import('@bo-pages/point/PointAccumulatePage'));
// order
const OrderInformationPage = lazy(() => import('@bo-pages/order/OrderInformationPage'));
const OrderInformationDetail = lazy(() => import('@bo-pages/order/OrderInformationDetail'));
const BlueveryOrderUploadPage = lazy(() => import('@bo-pages/order/BlueveryOrderUploadPage'));
const TaxInvoiceInformationPage = lazy(() => import('@bo-pages/order/TaxInvoiceInformationPage'));
const ManualOrderUploadPage = lazy(() => import('@bo-pages/order/ManualOrderUploadPage'));
// payment
const BankManagementPage = lazy(() => import('@bo-pages/payment/BankManagementPage'));
const CardManagementPage = lazy(() => import('@bo-pages/payment/CardCompanyManagementPage'));
const InicisManagementPage = lazy(() => import('@bo-pages/payment/InicisManagementPage'));
// delivery
const DeliveryManagementPage = lazy(() => import('@bo-pages/delivery/DeliveryManagementPage'));
const DeliveryDetailPage = lazy(() => import('@bo-pages/delivery/DeliveryDetailPage'));
const ReturnDeliveryPage = lazy(() => import('@bo-pages/delivery/ReturnDeliveryPage'));
const TicketDeliveryManagementPage = lazy(() => import('@bo-pages/delivery/TicketDeliveryManagementPage'));
const ParkPointOrderPage = lazy(() => import('@bo-pages/delivery/ParkPointOrderPage'));
const ExchangeDeliveryManagementPage = lazy(() => import('src/pages/delivery/ExchangeDeliveryManagementPage'));
const ParkPointUseCompletePage = lazy(() => import('@bo-pages/delivery/ParkPointUseCompletePage'));
const DelayOrderRegistrationPage = lazy(() => import('@bo-pages/delivery/DelayOrderRegistrationPage'));
const DeliveryDelayInfoPage = lazy(() => import('@bo-pages/delivery/DeliveryDelayInfoPage'));
// vendor
const VendorManagementPage = lazy(() => import('@bo-pages/vendor/management/VendorManagementPage'));
const VendorRequestManagementDetailPage = lazy(() =>
  import('@bo-pages/vendor/management/VendorRequestManagementDetailPage')
);
const VendorRegisterManagementDetailPage = lazy(() =>
  import('@bo-pages/vendor/management/VendorApprovalManagementDetailPage')
);
const VendorViewDetailPage = lazy(() => import('@bo-pages/vendor/management/VendorViewDetailPage'));
const VendorAgreementPage = lazy(() => import('@bo-pages/vendor/agreement/VendorAgreementPage'));
const BusinessPersonPage = lazy(() => import('@bo-pages/vendor/BusinessPersonPage'));
const MakerManagementPage = lazy(() => import('@bo-pages/vendor/maker/MakerManagementPage'));
const MakerRequestDetailPage = lazy(() => import('@bo-pages/vendor/maker/MakerRequestDetailPage'));
const MakerRegisterDetailPage = lazy(() => import('@bo-pages/vendor/maker/MakerRegisterDetailPage'));
const MakerViewDetailPage = lazy(() => import('@bo-pages/vendor/maker/MakerViewDetailPage'));
// claim
const ReturnInfoPage = lazy(() => import('@bo-pages/claim/ReturnInfoPage'));
const ReturnInfoDetail = lazy(() => import('@bo-pages/claim/ReturnInfoDetail'));
const ExchangePage = lazy(() => import('@bo-pages/claim/ExchangePage'));
const ExchangeDetail = lazy(() => import('@bo-pages/claim/ExchangeDetail'));
const OrderCancelInfoPage = lazy(() => import('@bo-pages/claim/OrderCancelInfoPage'));
const OrderCancelInfoDetail = lazy(() => import('@bo-pages/claim/OrderCancelInfoDetail'));
// statistics
const MemberProfilePage = lazy(() => import('@bo-pages/statistics/member/MemberProfilePage'));
const MemberGradeStatusPage = lazy(() => import('@bo-pages/statistics/member/MemberGradeStatusPage'));
const MemberAffiliateStatusPage = lazy(() => import('@bo-pages/statistics/member/MemberSiteSaleStatusPage'));
const GoodRegCntStatusPage = lazy(() => import('@bo-pages/statistics/good/GoodRegCntStatusPage'));
const SalesDateStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesDateStatusPage'));
const SalesMemberStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesMemberStatusPage'));
const SalesPaymentStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesPaymentStatusPage'));
const SalesCategoryStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesCategoryStatusPage'));
const SalesBrandStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesBrandStatusPage'));
const SalesGoodOptionStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesGoodOptionStatusPage'));
const SalesPartnerStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesPartnerStatusPage'));
const SalesMdStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesMdStatusPage'));
const SalesManufStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesManufStatusPage'));
const SalesExhibitionStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesExhibitionStatusPage'));
const SalesPromotionStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesPromotionStatusPage'));
const SalesMarketingOrderStatusPage = lazy(() => import('@bo-pages/statistics/sales/SalesMarketingOrderStatusPage'));
const AdminCounselStatusPage = lazy(() => import('@bo-pages/statistics/customer/CustomerCounselStatusPage'));
const AdminCounselDetailStatusPage = lazy(() =>
  import('@bo-pages/statistics/customer/CustomerCounselDetailStatusPage')
);
const ResultStatusPage = lazy(() => import('@bo-pages/statistics/result/ResultStatusPage'));
const ResultSaleAmtTop30Page = lazy(() => import('@bo-pages/statistics/result/ResultSaleAmtTop30Page'));

const setSiteSubsidiary = async () => {
  const { result: siteList } = await siteApi.getSiteMgmtList({ skipSpinner: true });
  setRecoilExternalState(siteState, siteList);
  const { result: subsidiaryList } = await siteApi.getSubsidiaryCompanyList({ skipSpinner: true });
  setRecoilExternalState(subsidiaryState, subsidiaryList);

  return {};
};

const routes = createBrowserRouter(
  createRoutesFromElements(
    <Route path="" element={<RootRoute />}>
      <Route path="common/job-group-select" element={<SelectJobGroupPage />} />
      <Route path="" element={<Home />} errorElement={<ClientServerErrorPage />}>
        <Route path="" element={<MainPage />} />
        <Route path="system">
          <Route path="common-code" element={<CommonCodePage />} />
          <Route path="holiday" element={<HolidayPage />} />
          <Route path="batch-management" element={<BatchManagementPage />} />
          <Route path="batch-execute-management" element={<BatchExecutePage />} />
          <Route path="terms-management" element={<TermsManagementPage />} />
          <Route path="terms-management/:id" element={<TermsManagementDetail />} />
          <Route path="menu-management" element={<MenuManagementPage />} />
          <Route path="page-management" element={<PageManagementPage />} />
          <Route path="prohibited-word" element={<ProhibitedWordPage />} />
          <Route path="user-auth-mgmt" element={<JobGroupPage />} />
          <Route path="site-management" element={<SiteManagementPage />} />
          <Route path="site-management/:id" element={<SiteManagementEdit />} />
          <Route path="site-management/:id/detail" element={<SiteManagementDetail />} />
          <Route path="site-management-minor" element={<SiteManagementPage />} />
          <Route path="site-management-minor/:id/detail" element={<SiteManagementDetailMinor />} />
          <Route path="giftishow-management" element={<GiftishowManagementPage />} />
          <Route path="alim-talk-management" element={<AlimTalkManagementPage />} />
          <Route path="alim-talk-management/:id" element={<AlimTalkManagementDetail />} />
        </Route>
        <Route path="member">
          <Route path="member" element={<MemberPage />} />
          <Route path="member/:id" element={<MemberDetailPage />} />
          <Route path="grade" element={<MemberGradePage />} />
        </Route>
        <Route path="promo">
          <Route path="exhibition" element={<ExhibitionListPage />} />
          <Route path="exhibition/:id" element={<ExhibitionDetailPage />} loader={setSiteSubsidiary} />
          <Route path="exhibition/:id/:exhibitionNo" element={<ExhibitionDetailPage />} />
          <Route path="exhibition/integration/:id" element={<IntegrationExhibitionRegistrationPage />} />
          <Route path="exhibition/integration/:id/:exhibitionNo" element={<IntegrationExhibitionRegistrationPage />} />
          <Route path="exhibition/alliance/:id" element={<AllianceExhibitionDetailPage />} />
          <Route path="exhibition/alliance/:id/:exhibitionNo" element={<AllianceExhibitionDetailPage />} />
          <Route path="time-sale" element={<TimeSaleManagementPage />} />
          <Route path="event" element={<EventListPage />} />
          <Route path="event/:id" element={<EventDetailPage />} loader={setSiteSubsidiary} />
          <Route path="event/:id/:eventNo" element={<EventDetailPage />} />
          <Route path="promotion-management" element={<PromotionListPage />} />
          <Route path="promotion-management/:id" element={<PromotionDetailPage />} loader={setSiteSubsidiary} />
          <Route path="promotion-management/:id/:promotionNo" element={<PromotionDetailPage />} />
          <Route
            path="promotion-management/:promotionChangeRequestNo/history"
            element={<PromotionApprovalHistoryDetailPage />}
          />
          <Route path="promotion-approval-management" element={<PromotionApprovalManagementPage />} />
          <Route
            path="promotion-approval-management/:promotionChangeRequestNo"
            element={<PromotionApprovalDetailPage />}
            loader={setSiteSubsidiary}
          />
          <Route path="promotion-limit-management" element={<PromotionBaseLimitManagementPage />} />
          <Route path="promotion-limit-management/:id" element={<PromotionBaseLimitDetailPage />} />
          <Route path="promotion/dm-template" element={<DMTemplateManagementPage />} />
          <Route path="promotion/dm-template/:id" element={<DMTemplateDetailPage />} />
          <Route path="promotion/dm-template/:id/:dmTemplateNo" element={<DMTemplateDetailPage />} />
          <Route path="promotion/marketing-channel" element={<MarketingChannelPage />} />
          <Route path="promotion/marketing-channel/:id" element={<MarketingChannelDetailPage />} />
          <Route path="promotion/marketing-channel/:id/:marketingChannelNo" element={<MarketingChannelDetailPage />} />
        </Route>
        <Route path="good">
          <Route path="brand-management" element={<BrandPage />} />
          <Route path="delivery-area-management" element={<DeliveryAreaPage />} />
          <Route path="base">
            <Route path="class-official-announcement/management" element={<OfficialAnnouncementManagementPage />} />
            <Route path="official-announcement/inquiry" element={<OfficialAnnouncementInquiryPage />} />
            <Route path="standard-category/management" element={<StandardCategoryPage />} loader={setSiteSubsidiary} />
          </Route>
          <Route path="review">
            <Route path="management" element={<GoodReviewManagementPage />} />
            <Route path="management/:id" element={<GoodReviewDetailPage />} />
          </Route>
          <Route path="info">
            <Route path="management" element={<GoodManagementPage />} loader={setSiteSubsidiary} />
            <Route path="registration" element={<GoodRegistrationPage />} loader={setSiteSubsidiary} />
            <Route path="registration-group" element={<GroupGoodRegistrationPage />} loader={setSiteSubsidiary} />
            <Route path="registration-batch" element={<BatchGoodRegistrationPage />} loader={setSiteSubsidiary} />
            <Route path="temp-status/modification/:id" element={<TempGoodModificationPage />} />
            <Route path="temp-status/modification/:id/group" element={<TempGroupGoodModificationPage />} />
            <Route path="temp-status/modification/:id/approval" element={<TempGoodMdRmPage />} />
            <Route path="temp-status/modification/:id/approval/group" element={<TempGroupGoodMdRmPage />} />
            <Route path="temp-status/detail/:id" element={<TempGoodDetailPage />} />
            <Route path="temp-status/detail/:id/group" element={<TempGroupGoodDetailPage />} />
            <Route path="approved-status/detail/:id" element={<GoodDetailPage />} />
            <Route path="approved-status/detail/:id/group" element={<GroupGoodDetailPage />} />
            <Route path="approved-status/modification/:id" element={<GoodModificationPage />} />
            <Route path="approved-status/modification/:id/group" element={<GoodGroupModificationPage />} />
            <Route path="item/management" element={<ItemManagementPage />} />
            <Route path="ticket/management" element={<TicketGoodManagementPage />} />
            <Route path="ticket/modification/:id" element={<TicketModificationPage />} />
            <Route path="sold-out/management" element={<SoldOutRequestManagePage />} />
          </Route>
          <Route path="approval">
            <Route path="history" element={<ApprovalHistoryManagementPage />} />
            <Route path="management-md" element={<ApprovalManagementMdPage />} />
            <Route path="management-rm" element={<ApprovalManagementRmPage />} />
          </Route>
        </Route>
        <Route path="display">
          <Route path="main-template">
            <Route path="management" element={<MainTemplatePage />} />
            <Route path="management/:id" element={<MainTemplateRegistration />} />
            <Route path="management/content/:id" element={<MainTemplateContent />} />
          </Route>
          <Route path="display-category-template">
            <Route path="management" element={<DisplayCategoryPage />} />
            <Route path="management/:id" element={<DisplayCategoryTemplateRegistration />} />
            <Route path="management/category/:id" element={<DisplayCategoryModification />} />
          </Route>
          <Route path="banner">
            <Route path={'management'} element={<BannerManagementPage />} />
            <Route path={'management/:id'} element={<BannerRegistrationPage />} loader={setSiteSubsidiary} />
          </Route>
        </Route>
        <Route path="customer-service">
          <Route path="faq">
            <Route path="management" element={<FaqManagementPage />} />
            <Route path="management/:id" element={<FaqRegistrationPage />} />
          </Route>
          <Route path="counseling">
            <Route path="management" element={<CounselingManagementPage />} />
            <Route path="management/add/:id" element={<CounselingRegistrationPage />} />
            <Route path="management/:id" element={<CounselingRegistrationPage />} />
            <Route path="approval">
              <Route path="management" element={<CounselingApprovalManagementPage />} />
              <Route path="management/:id" element={<CounselingApprovalRegistrationPage />} />
            </Route>
            <Route path="phrase">
              <Route path="management" element={<CounselingPhraseManagementPage />} />
              <Route path="management/:id" element={<CounselingPhraseRegistrationPage />} />
            </Route>
          </Route>
          <Route path="outbound">
            <Route path="management" element={<OutboundManagementPage />} />
            <Route path="management/:id" element={<OutboundDetailPage />} />
          </Route>
          <Route path="notification">
            <Route path="management" element={<NotificationPage />} />
            <Route path="management/:id" element={<NotificationRegistrationPage />} />
          </Route>
        </Route>
        <Route path="mall-question">
          <Route path="entry">
            <Route path="management" element={<EntryQuestionPage />} />
            <Route path="management/:id" element={<EntryQuestionDetailPage />} />
          </Route>
          <Route path="bulk-purchase">
            <Route path="management" element={<BulkPurchaseQuestionPage />} />
            <Route path="management/:id" element={<BulkPurchaseQuestionDetailPage />} />
          </Route>
          <Route path="alliance">
            <Route path="management" element={<AllianceQuestionPage />} />
            <Route path="management/:id" element={<AllianceQuestionDetailPage />} />
          </Route>
          <Route path="charger">
            <Route path="management" element={<ChargerManagementPage />} />
          </Route>
        </Route>
        <Route path="settlement">
          <Route path="good-settlement" element={<GoodSettlementPage />} />
          <Route path="carryover" element={<SettlementCarryOverPage />} />
          <Route path="manual-settlement" element={<ManualSettlementPage />} />
          <Route path="monthly-close" element={<MonthlySettlementPage />} />
          <Route path="vendor-close" element={<VendorSettlementClosePage />} />
          <Route path="bluevery" element={<BlueverySettlementPage />} />
        </Route>
        <Route path="point">
          <Route path="point-group-management" element={<PointGroupManagementPage />} />
          <Route path="point-group-management/:id" element={<PointGroupManagementDetail />} />
          <Route path="point-approval-management" element={<PointApprovalManagementPage />} />
          <Route path="point-approval-management/:id" element={<PointApprovalManagementDetail />} />
          <Route path="point-registration/saving" element={<PointRegistrationPage />} />
          <Route path="point-registration/saving/:id" element={<PointRegistrationPage />} />
          <Route path="point-registration/subtract/" element={<PointSubtractRegistrationPage />} />
          <Route path="point-registration/subtract/:id" element={<PointSubtractRegistrationPage />} />
          <Route path="point-registration/renewal/" element={<PointRenewalRegistrationPage />} />
          <Route path="point-registration/renewal/:id" element={<PointRenewalRegistrationPage />} />
          <Route path="save-info" element={<PointSaveInfoPage />} />
          <Route path="member-save-info" element={<PointMemberSaveInfoPage />} />
          <Route path="approval-template" element={<ApprovalTemplatePage />} />
          <Route path="approval-template/:id" element={<ApprovalTemplateDetail />} />
          <Route path="point-accumulate" element={<PointAccumulatePage />} />
        </Route>
        <Route path="order">
          <Route path="order-info" element={<OrderInformationPage />} />
          <Route path="order-info/:id" element={<OrderInformationDetail />} />
          <Route path="bluevery-order-upload" element={<BlueveryOrderUploadPage />} />
          <Route path="tax-invoice-info" element={<TaxInvoiceInformationPage />} />
          <Route path="manual-order-upload" element={<ManualOrderUploadPage />} />
        </Route>
        <Route path="payment">
          <Route path="bank" element={<BankManagementPage />} />
          <Route path="card" element={<CardManagementPage />} />
          <Route path="inicis" element={<InicisManagementPage />} />
        </Route>
        <Route path="delivery">
          <Route path="management" element={<DeliveryManagementPage />} />
          <Route path="management/:id" element={<DeliveryDetailPage />} />
          <Route path="ticket-management" element={<TicketDeliveryManagementPage />} />
          <Route path="park-point-order" element={<ParkPointOrderPage />} />
          <Route path="park-point-use-complete" element={<ParkPointUseCompletePage />} />
          <Route path="delay-order-registration" element={<DelayOrderRegistrationPage />} />
          <Route path="delivery-delay-info" element={<DeliveryDelayInfoPage />} />

          <Route path="change">
            <Route path="management" element={<ExchangeDeliveryManagementPage />} />
          </Route>
          <Route path="return">
            <Route path="management" element={<ReturnDeliveryPage />} />
          </Route>
        </Route>
        <Route path="vendor">
          <Route path="vendor-request-management" element={<VendorManagementPage key="vendor-request-management" />} />
          <Route path="vendor-request-management/:id" element={<VendorRequestManagementDetailPage />} />
          <Route
            path="vendor-approval-management"
            element={<VendorManagementPage key="vendor-approval-management" />}
          />
          <Route path="vendor-approval-management/:id" element={<VendorRegisterManagementDetailPage />} />
          <Route path="vendor-view" element={<VendorManagementPage activeAdd={false} key="vendor-view" />} />
          <Route path="vendor-view/:id" element={<VendorViewDetailPage />} />
          <Route path="vendor-agreement" element={<VendorAgreementPage />} />
          <Route path="business-person-management" element={<BusinessPersonPage />} />
          <Route
            path="maker-request-management"
            element={<MakerManagementPage activeAdd key="maker-request-management" />}
          />
          <Route path="maker-request-management/:id" element={<MakerRequestDetailPage />} />
          <Route
            path="maker-register-management"
            element={<MakerManagementPage activeAdd key="maker-register-management" />}
          />
          <Route path="maker-register-management/:id" element={<MakerRegisterDetailPage />} />
          <Route path="maker-view" element={<MakerManagementPage activeAdd={false} key="maker-view" />} />
          <Route path="maker-view/:id" element={<MakerViewDetailPage />} />
        </Route>
        <Route path="claim">
          <Route path="return-info" element={<ReturnInfoPage />} />
          <Route path="return-info/:id" element={<ReturnInfoDetail />} />
          <Route path="exchange" element={<ExchangePage />} />
          <Route path="exchange-info/:id" element={<ExchangeDetail />} />
          <Route path="order-cancel-info" element={<OrderCancelInfoPage />} />
          <Route path="order-cancel-info/:id" element={<OrderCancelInfoDetail />} />
        </Route>
        <Route path="statistics">
          <Route path="member">
            <Route path="profile" element={<MemberProfilePage />} />
            <Route path="grade-status" element={<MemberGradeStatusPage />} />
            <Route path="affiliate-status" element={<MemberAffiliateStatusPage />} />
          </Route>
          <Route path="good">
            <Route path="register-count-status" element={<GoodRegCntStatusPage />} />
          </Route>
          <Route path="sales">
            <Route path="date-status" element={<SalesDateStatusPage />} />
            <Route path="member-status" element={<SalesMemberStatusPage />} />
            <Route path="payment-status" element={<SalesPaymentStatusPage />} />
            <Route path="category-status" element={<SalesCategoryStatusPage />} />
            <Route path="brand-status" element={<SalesBrandStatusPage />} />
            <Route path="good-option-status" element={<SalesGoodOptionStatusPage />} />
            <Route path="partner-status" element={<SalesPartnerStatusPage />} />
            <Route path="md-status" element={<SalesMdStatusPage />} />
            <Route path="manuf-status" element={<SalesManufStatusPage />} />
            <Route path="exhibition-status" element={<SalesExhibitionStatusPage />} />
            <Route path="promotion-status" element={<SalesPromotionStatusPage />} />
            <Route path="marketing-order-status" element={<SalesMarketingOrderStatusPage />} />
          </Route>
          <Route path="admin">
            <Route path="counsel-status" element={<AdminCounselStatusPage />} />
            <Route path="counsel-detail-status" element={<AdminCounselDetailStatusPage />} />
          </Route>
          <Route path="result">
            <Route path="wmall-member" element={<ResultStatusPage />} />
            <Route path="site-sale-amt-top30" element={<ResultSaleAmtTop30Page />} />
          </Route>
        </Route>
      </Route>
      <Route path="logout" element={<LogoutPage />} />
      <Route path="unauthorized" element={<UnauthorizedErrorPage />} />
      <Route path="not-found" element={<NotFoundErrorPage />} />
      <Route path="error" element={<ClientServerErrorPage />} />
      <Route path="grid-test" element={<GridTestPage />} />
      <Route path="*" element={<NotFoundErrorPage />} />
    </Route>
  )
);

export default routes;
